import { getAddress, type Address, type Hex } from 'viem'
import type { PermitJson } from '../api/types'
import { FLARE_CHAIN_ID, config } from './config'

/**
 * The signed Permit2 `PermitSingle` that replaces the on-chain `Permit2.approve(token, router)`.
 *
 * The user signs it (no gas), `/swap` puts it at the front of the plan as `PERMIT2_PERMIT`,
 * and the router hands it to Permit2 inside the swap transaction, right before the
 * `PERMIT2_TRANSFER_FROM` that spends it. The allowance it sets then stays on chain, so the
 * next swap of the same token needs no signature until it nears expiry.
 *
 * The domain is Flare's Permit2, **not** the canonical address `@uniswap/permit2-sdk`
 * hard-codes: Permit2 is its own EIP-712 `verifyingContract`, and a signature over the
 * canonical address is rejected on chain with nothing to say why. Permit2's domain has no
 * `version`.
 */

/**
 * How long the allowance a permit sets stays spendable.
 *
 * Bounded, unlike the on-chain approval this replaces (which used `uint48` max): renewing
 * costs a signature rather than a transaction, so there is no reason to leave a standing
 * allowance open forever. Thirty days matches Uniswap's interface.
 */
export const PERMIT_EXPIRATION_SECONDS = 30 * 24 * 60 * 60

/**
 * How long the signature itself may be submitted.
 *
 * The API refuses a permit whose signature lapses before the swap's own deadline, and that
 * deadline can be up to `SWAP_DEFAULTS.maxDeadlineSeconds` (30 minutes) out. An hour keeps a
 * signature usable for about half an hour after it is made; `useApprovals` asks for a fresh
 * one after that rather than sending one the API will refuse.
 */
export const PERMIT_SIG_DEADLINE_SECONDS = 60 * 60

export const permit2Domain = {
  name: 'Permit2',
  chainId: FLARE_CHAIN_ID,
  verifyingContract: config.permit2,
} as const

export const permitSingleTypes = {
  PermitSingle: [
    { name: 'details', type: 'PermitDetails' },
    { name: 'spender', type: 'address' },
    { name: 'sigDeadline', type: 'uint256' },
  ],
  PermitDetails: [
    { name: 'token', type: 'address' },
    { name: 'amount', type: 'uint160' },
    { name: 'expiration', type: 'uint48' },
    { name: 'nonce', type: 'uint48' },
  ],
} as const

export type SignedPermit = {
  details: { token: Address; amount: bigint; expiration: number; nonce: number }
  spender: Address
  sigDeadline: bigint
  signature: Hex
}

/** The `/swap` wire shape: uint160 and uint256 as decimal strings, the two uint48s as numbers. */
export function permitJson(permit: SignedPermit): PermitJson {
  return {
    details: {
      token: getAddress(permit.details.token),
      amount: permit.details.amount.toString(),
      expiration: permit.details.expiration,
      nonce: permit.details.nonce,
    },
    spender: getAddress(permit.spender),
    sigDeadline: permit.sigDeadline.toString(),
    signature: permit.signature,
  }
}
